import React, { useEffect, useRef, useState } from "react";

import { SEND_DAYS, STAGES, channelLabel, dateAfter, formatDay, stageLabel } from "../../campaignUtils";
import { QuestionIcon } from "../../shared/StudioControls";
import { getSelectedLanguages } from "../campaignDetails.utils";

// Campaign facts: scenario description, audience, channels, languages and schedule.
const CampaignFacts = ({ campaign, cycleStart }) => {
  const payload = campaign.payload;
  const settings = payload.settings;
  const days = SEND_DAYS;

  return (
    <React.Fragment>
      {(payload.scenario.description || payload.scenario.explanation) && (
        <p className="cs-summary__description">{payload.scenario.description || payload.scenario.explanation}</p>
      )}
      <dl className="cs-summary__facts">
        <div>
          <dt>Customer group</dt>
          <dd>
            {`${payload.scenario.title} eligible customers`}
            <span className="cs-muted cs-block">
              Calculated separately for each day
            </span>
          </dd>
        </div>
        <div>
          <dt>Channels</dt>
          <dd>{settings ? settings.channels.map(channelLabel).join(", ") : "Not chosen"}</dd>
        </div>
        <div>
          <dt>Languages</dt>
          <dd>
            {settings
              ? settings.language_mode === "preferred"
                ? "Customer’s preferred language"
                : getSelectedLanguages(settings).join(", ")
              : "Not chosen"}
          </dd>
        </div>
        <div>
          <dt>Schedule</dt>
          <dd>
            <ul className="cs-plain-list">
              {days.map((day, index) => (
                <li key={day}>
                  {stageLabel(STAGES[index])}{cycleStart ? ` · ${formatDay(dateAfter(cycleStart, day - 1))}` : ""}
                </li>
              ))}
            </ul>
          </dd>
        </div>
      </dl>
    </React.Fragment>
  );
};

// Red "?" next to the campaign title. Hovering previews the campaign details;
// clicking opens them as a popup that stays until closed, Escape or an outside click.
export const CampaignSummary = ({ campaign, cycleStart }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const rootRef = useRef(null);
  const open = hovered || pinned;

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = event => {
      if (event.key === "Escape") {
        setPinned(false);
        setHovered(false);
      }
    };
    const handlePointer = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setPinned(false);
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className="cs-info"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className={`cs-info__button ${open ? "is-open" : ""}`}
        aria-label="Notification details"
        aria-expanded={open}
        aria-controls="cs-campaign-info"
        onClick={() => setPinned(value => !value)}
      >
        <QuestionIcon />
      </button>
      {open && (
        <div id="cs-campaign-info" role="dialog" aria-label="Notification details" className={`cs-info__panel ${pinned ? "is-pinned" : ""}`}>
          <div className="cs-info__card">
            <div className="cs-info__header">
              <p className="cs-info__title">Notification details</p>
              {pinned && (
                <button type="button" className="cs-info__close" aria-label="Close notification details" onClick={() => { setPinned(false); setHovered(false); }}>
                  ×
                </button>
              )}
            </div>
            <CampaignFacts campaign={campaign} cycleStart={cycleStart} />
          </div>
        </div>
      )}
    </span>
  );
};
